import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import type { Hotel } from 'frontend-utils/models';
import type { AppliedMappingFilterIds } from 'lumen/components/settings/central-mapping/dropdowns/dropdown-mapping-filter';
import type { DropdownMappingMoreActionsOptionType } from 'lumen/components/settings/central-mapping/dropdowns/dropdown-mapping-more-actions';
import type { CentralMappingCategory, CostSettingProfile } from 'lumen/models';
import { USER_ENGAGEMENT_ELEMENT_IDS } from 'lumen/models/seen-user-engagement-element';
import type HotelSettingsCentralMappingMappingRoute from 'lumen/routes/hotel/settings/central-mapping/mapping';
import type { MappingEntriesResponseMeta } from 'lumen/serializers/central-mapping/mapping-entry';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  type BarBasedOptionId,
  type Breakdown,
  BREAKDOWN_TRANSLATIONS,
  BREAKDOWNS,
  CENTRAL_MAPPING_BAR_BASED_OPTION_ID,
  CENTRAL_MAPPING_COMMISSIONABLE_OPTION_ID,
  CENTRAL_MAPPING_HELP_CENTER_LINKS,
  CENTRAL_MAPPING_INCLUDE_ARCHIVED_OPTIONS,
  CENTRAL_MAPPING_LOCAL_STORAGE_KEY_USER_SELECTED_PAGE_SIZE,
  CENTRAL_MAPPING_MORE_ACTIONS_OPTIONS_ID,
  CENTRAL_MAPPING_SORT_DIRECTION_OPTION_ID,
  CENTRAL_MAPPING_VIEW_FILTER_OPTION_ID,
  type CentralMappingFilterQueryParams,
  type CentralMappingMappingEntryExtendedModel,
  type CommissionableOptionId,
  DEFAULT_BREAKDOWN,
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
  DEFAULT_VALUE_TYPE,
  FILTER_OPTIONS,
  type FilterOption,
  type MappingManagedByType,
  type PmsValueType,
  type SortDirectionOptionId,
  VIEW_ID_TO_ANALYTICS_LABEL,
  type ViewFilterOptionId,
} from 'lumen/utils/central-mapping-constants';
import type { ModelFrom } from 'lumen/utils/routes';

export default class HotelSettingsCentralMappingMappingController extends Controller {
  declare model: ModelFrom<HotelSettingsCentralMappingMappingRoute>;

  @service declare access: Services['access'];
  @service declare alerting: Services['alerting'];
  @service declare centralMapping: Services['central-mapping'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intercom: Services['intercom'];
  @service declare intl: Services['intl'];
  @service declare localStorage: Services['local-storage'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];
  @service declare userEngagement: Services['user-engagement'];
  @tracked barBased: BarBasedOptionId = CENTRAL_MAPPING_BAR_BASED_OPTION_ID.ALL;
  @tracked breakdown: Breakdown = DEFAULT_BREAKDOWN;
  @tracked categories: string | null = null;
  @tracked commissionable: CommissionableOptionId =
    CENTRAL_MAPPING_COMMISSIONABLE_OPTION_ID.ALL;

  @tracked filterOption: FilterOption = FILTER_OPTIONS.ALL;
  @tracked includeArchived: boolean = false; // Passed to the API to include archived mapping entries
  @tracked isShowingReviMappingTypeModal = false;
  @tracked isShowingUploadModal = false;

  @tracked managedBy: MappingManagedByType | undefined = undefined; // Used for internal testing
  @tracked page = DEFAULT_PAGE;
  @tracked pageSize = DEFAULT_PAGE_SIZE;
  @tracked search: string = '';
  @tracked selectedMappingEntries: CentralMappingMappingEntryExtendedModel[] =
    [];

  @tracked sortDirection: SortDirectionOptionId =
    CENTRAL_MAPPING_SORT_DIRECTION_OPTION_ID.ASCENDING;
  @tracked uploadErrorMessage: string | undefined = undefined;
  @tracked valueType: PmsValueType = DEFAULT_VALUE_TYPE;
  @tracked view: ViewFilterOptionId = CENTRAL_MAPPING_VIEW_FILTER_OPTION_ID.ALL;

  updateMappingTask = task({ restartable: true }, async () => {
    const {
      mapping,
      breakdown,
      valueType,
      pageSize,
      page,
      includeArchived,
      managedBy,
    } = this;

    const dirtyMappingEntries = mapping.filter(
      (mappingEntry) => mappingEntry.isUnsaved,
    );

    try {
      await this.centralMapping.updateMappingTask.perform({
        mappingEntries: dirtyMappingEntries,
        breakdown,
        valueType: this.valueType,
        managedBy: this.managedBy,
      });

      if (breakdown === BREAKDOWNS.ROOM_TYPE) {
        // CeMa exposes room type mapping under the hotel resource
        // Therefore, on update, we re-request the hotel resource to have the latest room type mapping so that it's reflected in other products/features (eg. pricing manager calendar selections)
        await this.store.query<Hotel>('hotel', {
          id: this.hotelAndMarketService.hotel.id,
        });
      }

      const filterOptions: CentralMappingFilterQueryParams = {
        orderBy: `value_${this.sortDirection}`,
        view: this.view,
        search: this.search,
        barBased: this.barBased,
        commissionable: this.commissionable,
        categories: this.categories || undefined,
      };

      if (this.categories) {
        filterOptions.categories = this.categories;
      }

      void this.model.mapping.perform({
        breakdown,
        valueType,
        pageSize,
        page,
        filterOptions,
        managedBy,
        includeArchived,
      });

      this.toast.success(
        this.intl.t(
          this.isStayRestrictionRateGroupBreakdown
            ? 'settings.central_mapping.stay_restriction_rate_group.toast.success'
            : 'settings.central_mapping.hierarchy.toast.success',
        ),
        {
          showIcon: true,
        },
      );

      const hasSelectedRows = this.selectedMappingEntries.length > 0;

      if (hasSelectedRows) {
        this.setSelectedMappingEntries([]);
      }
    } catch (error) {
      this.errorTracking.captureException(error);

      // Show retry alert in a different task, otherwise the save button will stay in a loading state while showing the alert.
      void this.centralMapping.showRetryAlertTask.perform(
        this.updateMappingTask,
      );
    }

    return null;
  });

  uploadMappingTask = task(
    { drop: true },
    async (file: File): Promise<void> => {
      const { breakdown, valueType } = this;

      this.uploadErrorMessage = undefined;

      const isConfirmed = await this.alerting.showConfirmWarningAlert({
        title: this.intl.t(
          'settings.central_mapping.upload_mapping.confirmation.title',
        ),
        text: this.intl.t(
          'settings.central_mapping.upload_mapping.confirmation.body',
        ),
        buttons: {
          cancel: {
            text: this.intl.t('general.cancel'),
          },
          confirm: {
            text: this.intl.t('general.confirm'),
          },
        },
      });

      if (!isConfirmed) {
        return;
      }

      this.usageAnalytics.track('Mapping Upload Confirmed', {
        eventTrigger: 'upload-confirmed',
        breakdown,
        valueType,
      });

      try {
        await this.centralMapping.uploadMappingEntries(
          file,
          breakdown,
          valueType,
        );

        this.isShowingUploadModal = false;

        this.usageAnalytics.track('Mapping Upload Succeeded', {
          eventTrigger: 'upload-succeeded',
          breakdown,
          valueType,
        });

        this.toast.success(this.intl.t('general.changes_saved'), {
          showIcon: true,
        });

        const filterOptions = {
          orderBy: `value_${this.sortDirection}`,
          view: this.view,
          search: this.search,
          barBased: this.barBased,
          commissionable: this.commissionable,
          categories: this.categories || undefined,
        };

        void this.model.mapping.perform({
          breakdown,
          valueType,
          pageSize: this.pageSize,
          page: this.page,
          filterOptions,
          managedBy: this.managedBy,
          includeArchived: this.includeArchived,
        });
      } catch (error) {
        this.usageAnalytics.track('Mapping Upload Failed', {
          eventTrigger: 'upload-failed',
          breakdown,
          valueType,
        });

        this.uploadErrorMessage =
          this.centralMapping.getUploadErrorMessage(error);
      }
    },
  );

  get appliedFilters(): AppliedMappingFilterIds {
    return {
      barBasedId: this.barBased,
      commissionableId: this.commissionable,
      categoryIds: this.categories?.split(',') ?? [],
      sortDirectionId: this.sortDirection,
      viewId: this.view,
      search: this.search,
      includeArchivedId: this.includeArchived
        ? CENTRAL_MAPPING_INCLUDE_ARCHIVED_OPTIONS.SHOW
        : CENTRAL_MAPPING_INCLUDE_ARCHIVED_OPTIONS.HIDE,
    };
  }

  get costProfiles(): CostSettingProfile[] {
    return this.model.costProfiles.last?.value ?? [];
  }

  get curentPageSizeValueOption(): { label: string; value: number } {
    const pageSizeToUse =
      this.model.mapping.last?.value?.paginationMeta?.pageSize ?? this.pageSize;
    return {
      value: pageSizeToUse,
      label: this.intl.t('settings.central-mapping.mapping.page_no_of_rows', {
        noOfRows: this.intl.formatNumber(pageSizeToUse),
      }),
    };
  }

  get hasChanges(): boolean {
    return this.mapping.some((mappingEntry) => mappingEntry.isUnsaved);
  }

  get hasMarketMappingAccess(): boolean {
    return (
      this.access.hasCentralMappingMarketViewAccess ||
      this.access.hasCentralMappingMarketEditAccess
    );
  }

  get hasMarketMappingEditAccess(): boolean {
    return this.access.hasCentralMappingMarketEditAccess;
  }

  // Only once the profiles loaded, so the nudge doesn't flash while they load.
  get hasNoCostProfiles(): boolean {
    const lastRun = this.model.costProfiles.last;

    return (
      this.isCostManagedByMarket &&
      this.breakdown === BREAKDOWNS.DISTRIBUTION_CHANNEL &&
      !!lastRun?.isSuccessful &&
      lastRun.value?.length === 0
    );
  }

  get hierarchy(): CentralMappingCategory[] {
    return this.model.hierarchy.last?.value?.hierarchy ?? [];
  }

  // Gated on the market mapping access, the closest right there is today. Swap it for the net revenue
  // access right, and the `NET_REVENUE_SETTINGS` toggle for the net revenue feature flag, once they exist.
  get isCostManagedByMarket(): boolean {
    return !!this.spiderMarketId;
  }

  get isMappingManagedByMarket(): boolean {
    return (
      this.hasMarketMappingAccess &&
      !!this.hotelAndMarketService.hotel.spiderMarketId
    );
  }

  get isMappingTypeModalAvailable(): boolean {
    return !this.hotelAndMarketService.hotel?.spiderMarketId;
  }

  get isMarketSegmentBreakdown(): boolean {
    return this.breakdown === BREAKDOWNS.MARKET_SEGMENT;
  }

  get isPageLoading(): boolean {
    return !!this.model.mapping?.last?.isRunning || this.isSaving;
  }

  get isReadOnly(): boolean {
    return !this.access.hasCentralMappingEditAccess;
  }

  get isSaving(): boolean {
    return this.updateMappingTask.isRunning;
  }

  get isStayRestrictionRateGroupBreakdown(): boolean {
    return this.breakdown === BREAKDOWNS.STAY_RESTRICTION_RATE_GROUP;
  }

  get mapping(): CentralMappingMappingEntryExtendedModel[] {
    return this.model.mapping.last?.value?.mappingEntries ?? [];
  }

  get mappingPageBadgeText(): string {
    if (this.isMappingManagedByMarket) {
      return this.intl.t(
        'settings.central_mapping.read_only.portfolio_managed',
      );
    }
    return this.intl.t('settings.central_mapping.read_only.view_only');
  }

  get mappingPaginationData(): MappingEntriesResponseMeta['paginationMeta'] {
    return this.model.mapping.lastSuccessful?.value?.paginationMeta;
  }

  get moreActionsOptions(): DropdownMappingMoreActionsOptionType[] {
    if (this.isStayRestrictionRateGroupBreakdown) {
      return [];
    }

    const options: DropdownMappingMoreActionsOptionType[] = [
      {
        id: CENTRAL_MAPPING_MORE_ACTIONS_OPTIONS_ID.HELP_CENTER,
        label: this.intl.t('general.help_center'),
        icon: 'book',
        unsafe_isLink: true,
      },
    ];

    if (!this.isReadOnly) {
      if (
        this.breakdown === BREAKDOWNS.MARKET_SEGMENT &&
        this.isMappingTypeModalAvailable
      ) {
        options.unshift({
          id: CENTRAL_MAPPING_MORE_ACTIONS_OPTIONS_ID.MAPPING_TYPE,
          label: this.intl.t(
            'settings.central_mapping.temporary_mapping_type_settings.title',
          ),
          icon: 'settings',
        });
      }
      options.unshift({
        id: CENTRAL_MAPPING_MORE_ACTIONS_OPTIONS_ID.NAVIGATE_TO_HIERARCHY,
        label: this.navigateToHierarchyButtonLabel,
        icon: this.isReadOnly ? undefined : 'edit-pencil',
      });
    }
    return options;
  }

  get navigateToHierarchyButtonLabel(): string {
    if (this.isReadOnly) {
      return this.intl.t('settings.central_mapping.view.hierarchy');
    }
    return this.intl.t('settings.central_mapping.edit.hierarchy');
  }

  get navigateToMarketButtonLabel(): string {
    if (this.hasMarketMappingEditAccess) {
      return this.intl.t('settings.central_mapping.navigate_to_market.edit');
    }
    return this.intl.t('settings.central_mapping.navigate_to_market.view');
  }

  get showMappingPageBadge(): boolean {
    if (this.isStayRestrictionRateGroupBreakdown) {
      return (
        !this.access.hasPricingAssistantRestrictionsSettingsEditRights &&
        !this.model.mapping?.last?.isRunning
      );
    }
    return this.isReadOnly && !this.model.mapping?.last?.isRunning;
  }

  get showUserOnboardingModal(): boolean {
    return (
      !this.isPageLoading &&
      !this.isReadOnly &&
      !this.userEngagement.hasSeen(
        USER_ENGAGEMENT_ELEMENT_IDS.CENTRAL_MAPPING_MAPPING_ONBOARDING_MODAL,
      )
    );
  }

  // The Spider market whose cost profiles the hotel uses, when the user can see that market's mapping.
  get spiderMarketId(): string | null {
    return this.centralMapping.costProfilesMarketId;
  }

  get title(): string {
    if (this.isStayRestrictionRateGroupBreakdown) {
      return this.intl.t(
        'settings.central_mapping.mapping.title.stay_restriction_rate_group',
      );
    }

    const translation = BREAKDOWN_TRANSLATIONS[this.breakdown];
    const mapping = this.intl.t('settings.pms_mapping.mapping');
    const mappingType = translation
      ? `${this.intl.t(translation)}`
      : `${this.intl.t(BREAKDOWN_TRANSLATIONS[BREAKDOWNS.ROOM_TYPE])}`;
    const title = this.intl.t('settings.central_mapping.hierarchy.title', {
      mappingType,
      mapping,
    });

    return title;
  }

  @action
  async applyMappingFilters(
    appliedFilters: AppliedMappingFilterIds,
  ): Promise<void> {
    if (this.hasChanges) {
      const canProceedWithAction = await this.handleUnsavedChanges();

      if (!canProceedWithAction) {
        return;
      }
    }

    const {
      sortDirectionId,
      viewId,
      search,
      categoryIds,
      barBasedId,
      commissionableId,
      includeArchivedId,
    } = appliedFilters;

    if (sortDirectionId !== this.sortDirection) {
      this.usageAnalytics.track('Mapping Data Sorted', {
        eventTrigger: 'value-sorting',
        sortBy: 'value',
        sortDirection: sortDirectionId,
      });
    }

    if (sortDirectionId) {
      this.sortDirection = sortDirectionId;
    }

    if (includeArchivedId) {
      this.includeArchived =
        includeArchivedId === CENTRAL_MAPPING_INCLUDE_ARCHIVED_OPTIONS.SHOW;
    }

    if (viewId) {
      this.view = viewId;

      if (viewId !== CENTRAL_MAPPING_VIEW_FILTER_OPTION_ID.ALL) {
        this.usageAnalytics.track('Mapping Data Filtered', {
          eventTrigger: 'value-filter',
          filter: VIEW_ID_TO_ANALYTICS_LABEL[viewId],
        });
      }
    }

    if (barBasedId) {
      this.barBased = barBasedId;

      this.usageAnalytics.track('Mapping Data Filtered', {
        eventTrigger: 'value-filter',
        filter: 'bar-based',
      });
    }

    if (commissionableId) {
      this.commissionable = commissionableId;

      this.usageAnalytics.track('Mapping Data Filtered', {
        eventTrigger: 'value-filter',
        filter: 'commissionable',
      });
    }

    if (categoryIds || this.categories) {
      this.categories = categoryIds?.join(',') ?? null;

      this.usageAnalytics.track('Mapping Data Filtered', {
        eventTrigger: 'value-filter',
        filter: 'category',
      });
    }

    this.search = search ?? '';
  }

  @action clearUploadErrorMessage(): void {
    this.uploadErrorMessage = undefined;
  }

  @action closeUserOnboardingModal(): void {
    this.userEngagement.markAsSeen(
      USER_ENGAGEMENT_ELEMENT_IDS.CENTRAL_MAPPING_MAPPING_ONBOARDING_MODAL,
    );
  }

  @action
  discardChanges(): void {
    this.mapping.forEach((mappingEntry) => {
      mappingEntry.rollbackAttributes();
      mappingEntry.rollbackRelationships();
    });
  }

  @action
  async handleUnsavedChanges(): Promise<boolean> {
    let canProceedWithAction = this.isMarketSegmentBreakdown
      ? await this.centralMapping.showMarketSegmentationUnsavedChangesAlert(
          () => this.onClickSave(),
          this.valueType,
        )
      : await this.centralMapping.showUnsavedChangesAlert(() =>
          this.discardChanges(),
        );

    if (canProceedWithAction && this.isMarketSegmentBreakdown) {
      const isSuccessful = await this.centralMapping.waitForUpdateMappingTask();

      if (!isSuccessful) {
        canProceedWithAction = false;
      }
    }

    return canProceedWithAction;
  }

  @action handleUploadFile(file: File): void {
    void this.uploadMappingTask.perform(file);
  }

  @action
  async onChangePageSize(pageSize: number): Promise<void> {
    if (this.hasChanges) {
      const canProceedWithAction = await this.handleUnsavedChanges();

      if (!canProceedWithAction) {
        return;
      }
    }

    this.localStorage.setItem(
      CENTRAL_MAPPING_LOCAL_STORAGE_KEY_USER_SELECTED_PAGE_SIZE,
      pageSize.toString(),
    );
    this.page = DEFAULT_PAGE;
    this.pageSize = pageSize;

    this.usageAnalytics.track('Mapping Table Row Number Changed', {
      eventTrigger: 'change-row',
      pageSize: this.pageSize,
    });
  }

  @action
  async onClickDiscardChanges(): Promise<void> {
    if (this.hasChanges) {
      await this.centralMapping.showUnsavedChangesAlert(() =>
        this.discardChanges(),
      );

      this.setSelectedMappingEntries([]);
    }
  }

  @action onClickNavigateToHierarchy(): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.router.legacyTransitionTo('hotel.settings.central-mapping.hierarchy', {
      queryParams: { breakdown: this.breakdown },
    });

    this.usageAnalytics.track('Hierarchy Page Switched', {
      eventTrigger: 'switch-hierarchy',
    });
  }

  @action
  onClickNavigateToMarket(): void {
    const marketId = this.hotelAndMarketService.hotel.spiderMarketId!;

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.router.legacyTransitionTo(
      'market.settings.central-mapping.mapping',
      marketId,
      {
        queryParams: {
          breakdown: this.breakdown,
          // Set properties filter on the Market Mapping page to the current hotel id to only show mapping entries related to this hotel
          properties: this.hotelAndMarketService.hotel.id,
        },
      },
    );
  }

  @action
  onClickSave(): void {
    if (this.isStayRestrictionRateGroupBreakdown) {
      this.usageAnalytics.track('Rate Group Changed', {
        eventTrigger: 'rate-group-config',
        product: 'pricing-manager',
      });
    }

    void this.updateMappingTask.perform();
  }

  @action onClickUploadMapping(): void {
    this.isShowingUploadModal = true;
    this.uploadErrorMessage = undefined;
  }

  @action onCloseReviMappingTypeModal(): void {
    this.isShowingReviMappingTypeModal = false;
  }

  @action onCloseUploadModal(): void {
    this.isShowingUploadModal = false;
    this.uploadErrorMessage = undefined;
  }

  @action onSelectMoreActionsOption(
    option: DropdownMappingMoreActionsOptionType,
  ): void {
    switch (option.id) {
      case CENTRAL_MAPPING_MORE_ACTIONS_OPTIONS_ID.NAVIGATE_TO_HIERARCHY:
        this.onClickNavigateToHierarchy();
        break;
      case CENTRAL_MAPPING_MORE_ACTIONS_OPTIONS_ID.MAPPING_TYPE:
        this.isShowingReviMappingTypeModal = true;
        break;
      case CENTRAL_MAPPING_MORE_ACTIONS_OPTIONS_ID.HELP_CENTER:
        this.openHelpArticle();
        break;
      default:
        break;
    }
  }

  @action
  async onSetPage(page: number): Promise<void> {
    if (this.hasChanges) {
      const canProceedWithAction = await this.handleUnsavedChanges();

      if (!canProceedWithAction) {
        return;
      }
    }

    this.page = page;
    // Clear selection when changing page
    if (page !== this.mappingPaginationData?.page) {
      this.setSelectedMappingEntries([]);
    }

    this.usageAnalytics.track('Mapping Page Number Changed', {
      eventTrigger: 'change-pages',
      page: this.page,
    });
  }

  @action openHelpArticle(): void {
    this.intercom.redirectToIntercomHelp(
      CENTRAL_MAPPING_HELP_CENTER_LINKS.MAPPING_PAGE,
    );
  }

  @action resetMappingFilters(): void {
    this.barBased = CENTRAL_MAPPING_BAR_BASED_OPTION_ID.ALL;
    this.commissionable = CENTRAL_MAPPING_COMMISSIONABLE_OPTION_ID.ALL;
    this.sortDirection = CENTRAL_MAPPING_SORT_DIRECTION_OPTION_ID.ASCENDING;
    this.view = CENTRAL_MAPPING_VIEW_FILTER_OPTION_ID.ALL;
    this.search = '';
  }

  @action
  setFilterOption(filter: FilterOption): void {
    this.filterOption = filter;
  }

  @action
  setSelectedMappingEntries(
    mappingEntries: CentralMappingMappingEntryExtendedModel[],
  ): void {
    this.selectedMappingEntries = mappingEntries;
  }

  @action
  async setValueType(valueType: PmsValueType): Promise<void> {
    if (valueType !== this.valueType && this.hasChanges) {
      const canProceedWithAction = await this.handleUnsavedChanges();

      if (!canProceedWithAction) {
        return;
      }
    }

    this.valueType = valueType;
  }
}
